// src/components/TextField.tsx
import React from 'react';
import { cn } from '../../lib/utils';

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'tel';
  disabled?: boolean;
  className?: string;
  validation?: {
    pattern?: RegExp;
    minLength?: number;
    maxLength?: number;
    customValidation?: (value: string) => string | undefined;
  };
  // Add these new props
  component?: 'input' | 'textarea';
  rows?: number;
}

export const TextField: React.FC<TextFieldProps> = ({
  id,
  label,
  value,
  onChange,
  error,
  required = false,
  placeholder,
  type = 'text',
  disabled = false,
  className,
  validation,
  component = 'input',
  rows = 3
}) => {
  const [touched, setTouched] = React.useState(false);
  const isValid = touched && !error && value.length > 0;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  const handleBlur = () => {
    setTouched(true);
  };

  const getBorderStyles = () => {
    if (!touched || disabled) return "border-gray-300 focus:ring-blue-200";
    if (error) return "border-red-500 focus:ring-red-200";
    if (isValid) return "border-green-500 focus:ring-green-200";
    return "border-gray-300 focus:ring-blue-200";
  };

  const getLabelStyles = () => {
    if (!touched) return "text-gray-700";
    if (error) return "text-red-500";
    if (isValid) return "text-green-600";
    return "text-gray-700";
  };

  const inputClasses = cn(
    "block w-full rounded-30 border p-2",
    "focus:outline-none focus:ring-2 transition-colors duration-200",
    disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white",
    getBorderStyles(),
    className
  );

  return (
    <div className="space-y-2 relative">
      <label 
        htmlFor={id}
        className={cn(
          "block text-sm font-medium",
          getLabelStyles()
        )}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {component === 'textarea' ? (
          <textarea
            id={id}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            className={cn(inputClasses, "resize-none min-h-[72px]")}
          />
        ) : (
          <input
            id={id}
            type={type}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            className={inputClasses}
          />
        )}
        {touched && (isValid || error) && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            {isValid && !error ? (
              <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : error ? (
              <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : null}
          </div>
        )}
      </div>
      {error && touched && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
};